<template>
  <section class="admin-content" id="vendors-page">
    <Navbar />
    <main class="admin-main">

      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0;">
            <div class="header-icon-wrap mr-3">
              <i class="fe fe-truck" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">Vendors &amp; Stock</h4>
              <small style="opacity: .8;">Register vendors and manage their available drug stock</small>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <div class="row">

            <!-- Left: Register + Stock form -->
            <div class="col-md-5 mb-4">

              <!-- Register Vendor -->
              <div class="card shadow-sm form-card mb-4">
                <div class="form-card-header">
                  <div class="section-icon"><i class="fe fe-user-plus" style="font-size:13px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Register Vendor</h6>
                </div>
                <div class="card-body px-4 py-4">
                  <div class="form-row">
                    <div class="form-group col-md-12">
                      <label class="form-label">Name <span class="text-danger">*</span></label>
                      <input v-model="form.name" class="form-control styled-input" type="text" placeholder="Vendor name…" />
                    </div>
                    <div class="form-group col-md-6">
                      <label class="form-label">Phone</label>
                      <input v-phone v-model="form.phone_number" class="form-control styled-input" type="text" placeholder="+234…" />
                    </div>
                    <div class="form-group col-md-6">
                      <label class="form-label">Email</label>
                      <input v-model="form.email" class="form-control styled-input" type="email" placeholder="vendor@email.com" />
                    </div>
                    <div class="form-group col-md-12">
                      <label class="form-label">Address</label>
                      <textarea v-model="form.address" class="form-control styled-input" rows="2" placeholder="Street address…"></textarea>
                    </div>
                    <div class="form-group col-md-12 d-flex align-items-end">
                      <div class="custom-control custom-checkbox mb-2">
                        <input id="isGov" v-model="form.is_government" class="custom-control-input" type="checkbox" />
                        <label class="custom-control-label" for="isGov" style="font-size:.85rem;">Gov Vendor</label>
                      </div>
                    </div>
                  </div>
                  <button type="button" class="btn btn-submit btn-block" @click="createVendor">
                    <i class="fe fe-plus mr-1"></i>Create Vendor
                  </button>
                </div>
              </div>

              <!-- Add / Update Stock -->
              <div class="card shadow-sm form-card" v-if="selectedVendor">
                <div class="form-card-header">
                  <div class="section-icon"><i class="fe fe-layers" style="font-size:13px;"></i></div>
                  <div>
                    <h6 class="mb-0 font-weight-bold text-white">Update Stock</h6>
                    <small style="opacity:.75; font-size:.78rem;">{{ selectedVendor.name }}</small>
                  </div>
                </div>
                <div class="card-body px-4 py-4">
                  <div class="form-group">
                    <label class="form-label">Drug</label>
                    <select v-model="stockForm.drug_id" class="form-control styled-input">
                      <option value="">Select drug…</option>
                      <option v-for="drug in drugs" :key="drug.id" :value="drug.id">{{ drug.drug_name }}</option>
                    </select>
                  </div>
                  <div class="form-row">
                    <div class="form-group col-md-6">
                      <label class="form-label">Qty Available</label>
                      <input v-model.number="stockForm.quantity_available" class="form-control styled-input" type="number" min="0" />
                    </div>
                    <div class="form-group col-md-6">
                      <label class="form-label">Unit Price</label>
                      <div class="input-group">
                        <div class="input-group-prepend"><span class="input-group-text"><i class="mdi mdi-currency-ngn"></i></span></div>
                        <input v-model.number="stockForm.unit_price" class="form-control styled-input" type="number" min="0" step="0.01" />
                      </div>
                    </div>
                    <div class="form-group col-md-6">
                      <label class="form-label">Reorder Level</label>
                      <input v-model.number="stockForm.reorder_level" class="form-control styled-input" type="number" min="0" />
                    </div>
                  </div>
                  <button type="button" class="btn btn-submit btn-block" @click="upsertStock">
                    <i class="fe fe-save mr-1"></i>Save Stock Row
                  </button>
                </div>
              </div>

            </div>

            <!-- Right: Tables -->
            <div class="col-md-7">

              <!-- Vendors table -->
              <div class="card shadow-sm table-card mb-4">
                <div class="d-flex align-items-center justify-content-between px-4 pt-4 pb-3">
                  <div class="d-flex align-items-center">
                    <i class="fe fe-truck text-primary mr-2"></i>
                    <h6 class="mb-0 font-weight-bold">Vendors</h6>
                    <span class="badge badge-soft-info ml-2">{{ vendors.length }}</span>
                  </div>
                </div>
                <div class="table-responsive">
                  <table class="table align-td-middle mb-0">
                    <thead style="background:#f8fafc;">
                      <tr>
                        <th class="table-th">Name</th>
                        <th class="table-th">Phone</th>
                        <th class="table-th">Email</th>
                        <th class="table-th">Gov</th>
                        <th class="table-th"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="vendor in vendors" :key="vendor.id" class="table-row"
                        :class="{ 'row-selected': selectedVendor && selectedVendor.id === vendor.id }">
                        <td class="px-4 py-3 font-weight-bold" style="font-size:.88rem;">{{ vendor.name }}</td>
                        <td class="py-3 text-muted small">{{ vendor.phone_number || '—' }}</td>
                        <td class="py-3 text-muted small">{{ vendor.email || '—' }}</td>
                        <td class="py-3">
                          <span v-if="vendor.is_government" class="badge badge-soft-success" style="font-size:.72rem;">Gov</span>
                          <span v-else class="text-muted small">—</span>
                        </td>
                        <td class="py-3 pr-4">
                          <button class="btn btn-sm btn-submit" @click="selectVendor(vendor)">
                            <i class="fe fe-layers mr-1"></i>Stock
                          </button>
                        </td>
                      </tr>
                      <tr v-if="!vendors.length">
                        <td colspan="5" class="text-center py-5 text-muted">
                          <i class="fe fe-inbox" style="font-size:2rem;opacity:.3"></i>
                          <p class="mt-2 mb-0">No vendors registered</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Selected vendor: stock table -->
              <div class="card shadow-sm table-card mb-4" v-if="selectedVendor">
                <div class="d-flex align-items-center px-4 pt-4 pb-3">
                  <i class="fe fe-layers text-primary mr-2"></i>
                  <h6 class="mb-0 font-weight-bold">{{ selectedVendor.name }} — Stock</h6>
                  <span class="badge badge-soft-info ml-2">{{ stocks.length }}</span>
                </div>
                <div class="table-responsive">
                  <table class="table align-td-middle mb-0">
                    <thead style="background:#f8fafc;">
                      <tr>
                        <th class="table-th">Drug</th>
                        <th class="table-th">Qty</th>
                        <th class="table-th">Unit Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="stock in stocks" :key="stock.id" class="table-row">
                        <td class="px-4 py-3 font-weight-bold" style="font-size:.88rem;">
                          {{ stock.drug ? stock.drug.drug_name : stock.drug_id }}
                        </td>
                        <td class="py-3">
                          <span :class="stock.quantity_available <= (stock.reorder_level || 0) ? 'badge badge-soft-danger' : 'badge badge-soft-success'" style="font-size:.75rem;">
                            {{ stock.quantity_available }}
                          </span>
                        </td>
                        <td class="py-3"><i class="mdi mdi-currency-ngn"></i>{{ stock.unit_price }}</td>
                      </tr>
                      <tr v-if="!stocks.length">
                        <td colspan="3" class="text-center py-4 text-muted small">No stock entries yet</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Selected vendor: staff -->
              <div class="card shadow-sm form-card" v-if="selectedVendor">
                <div class="form-card-header">
                  <div class="section-icon"><i class="fe fe-users" style="font-size:13px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">{{ selectedVendor.name }} — Staff</h6>
                  <span class="badge badge-light ml-auto">{{ vendorStaff.length }}</span>
                </div>
                <div class="card-body px-4 py-4">
                  <div class="form-row">
                    <div class="form-group col-md-6">
                      <label class="form-label">First Name</label>
                      <input v-model="staffForm.firstname" class="form-control styled-input" type="text" />
                    </div>
                    <div class="form-group col-md-6">
                      <label class="form-label">Last Name</label>
                      <input v-model="staffForm.lastname" class="form-control styled-input" type="text" />
                    </div>
                    <div class="form-group col-md-6">
                      <label class="form-label">Phone</label>
                      <input v-phone v-model="staffForm.phone_number" class="form-control styled-input" type="text" />
                    </div>
                    <div class="form-group col-md-6">
                      <label class="form-label">Email</label>
                      <input v-model="staffForm.email" class="form-control styled-input" type="email" />
                    </div>
                    <div class="form-group col-md-6">
                      <label class="form-label">Password</label>
                      <input v-model="staffForm.password" class="form-control styled-input" type="password" />
                    </div>
                    <div class="form-group col-md-6">
                      <label class="form-label">Type</label>
                      <select v-model="staffForm.type" class="form-control styled-input">
                        <option value="vendor_staff">vendor_staff</option>
                        <option value="vendor_employee">vendor_employee</option>
                      </select>
                    </div>
                  </div>
                  <button type="button" class="btn btn-submit mb-4" @click="registerStaff">
                    <i class="fe fe-user-plus mr-1"></i>Register Staff
                  </button>

                  <div class="section-divider mb-3"><span class="section-divider-text">Current Staff</span></div>

                  <div class="table-responsive">
                    <table class="table align-td-middle mb-0">
                      <thead style="background:#f8fafc;">
                        <tr>
                          <th class="table-th">Name</th>
                          <th class="table-th">Type</th>
                          <th class="table-th">Email</th>
                          <th class="table-th">Phone</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="staff in vendorStaff" :key="staff.id" class="table-row">
                          <td class="px-4 py-2 font-weight-bold" style="font-size:.85rem;">{{ staff.firstname }} {{ staff.lastname }}</td>
                          <td class="py-2"><span class="badge badge-soft-info" style="font-size:.72rem;">{{ staff.type }}</span></td>
                          <td class="py-2 text-muted small">{{ staff.email || '—' }}</td>
                          <td class="py-2 text-muted small">{{ staff.phone_number || '—' }}</td>
                        </tr>
                        <tr v-if="!vendorStaff.length">
                          <td colspan="4" class="text-center py-3 text-muted small">No staff yet</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
let agency = JSON.parse(localStorage.getItem("agency"));

export default {
  components: { Navbar },
  data() {
    return {
      user: null,
      vendors: [],
      drugs: [],
      stocks: [],
      selectedVendor: null,
      vendorStaff: [],
      form: { agency_id: "", name: "", phone_number: "", email: "", address: "", is_government: false },
      stockForm: { drug_id: "", quantity_available: 0, unit_price: 0, reorder_level: "" },
      staffForm: { firstname: "", lastname: "", phone_number: "", email: "", password: "", type: "vendor_staff" },
    };
  },
  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user") || "null");
    this.fetchVendors();
    this.fetchDrugs();
  },
  methods: {
    fetchVendors() {
      this.axios.get("/api/v1/auth/vendors", { params: { per_page: 200, agency_id: agency?.id } }).then((res) => {
        this.vendors = res.data.data || [];
      });
    },
    fetchDrugs() {
      this.axios.get("/api/v1/auth/drugs", { params: { per_page: 500 } }).then((res) => {
        this.drugs = (res.data.data || res.data || []).filter(Boolean);
      });
    },
    createVendor() {
      this.axios
        .post("/api/v1/auth/vendors", { ...this.form, agency_id: agency?.id })
        .then(() => {
          this.$toasted.success("Vendor created", { position: "top-center", duration: 3000 });
          this.form = { agency_id: "", name: "", phone_number: "", email: "", address: "", is_government: false };
          this.fetchVendors();
        })
        .catch((err) => {
          this.$toasted.error((err.response && err.response.data && err.response.data.message) || "Failed to create vendor", { position: "top-center", duration: 3000 });
        });
    },
    selectVendor(vendor) {
      this.selectedVendor = vendor;
      this.fetchStocks(vendor.id);
      this.fetchStaff(vendor.id);
    },
    fetchStocks(vendorId) {
      this.axios.get(`/api/v1/auth/vendors/${vendorId}/stocks`, { params: { per_page: 200 } }).then((res) => {
        this.stocks = res.data.data || [];
      });
    },
    upsertStock() {
      if (!this.selectedVendor) return;
      const payload = {
        stocks: [{
          drug_id: this.stockForm.drug_id,
          quantity_available: this.stockForm.quantity_available,
          unit_price: this.stockForm.unit_price,
          reorder_level: this.stockForm.reorder_level || null,
        }],
      };
      this.axios
        .post(`/api/v1/auth/vendors/${this.selectedVendor.id}/stocks`, payload)
        .then(() => {
          this.$toasted.success("Stock saved", { position: "top-center", duration: 3000 });
          this.fetchStocks(this.selectedVendor.id);
        })
        .catch((err) => {
          this.$toasted.error((err.response && err.response.data && err.response.data.message) || "Failed to save stock", { position: "top-center", duration: 3000 });
        });
    },
    fetchStaff(vendorId) {
      this.axios.get(`/api/v1/auth/vendors/${vendorId}/staff`, { params: { per_page: 200 } }).then((res) => {
        this.vendorStaff = res.data.data || [];
      });
    },
    registerStaff() {
      if (!this.selectedVendor) return;
      this.axios
        .post(`/api/v1/auth/vendors/${this.selectedVendor.id}/staff`, this.staffForm)
        .then(() => {
          this.$toasted.success("Vendor staff created", { position: "top-center", duration: 3000 });
          this.staffForm = { firstname: "", lastname: "", phone_number: "", email: "", password: "", type: "vendor_staff" };
          this.fetchStaff(this.selectedVendor.id);
        })
        .catch((err) => {
          this.$toasted.error((err.response && err.response.data && err.response.data.message) || "Failed to create staff", { position: "top-center", duration: 3000 });
        });
    },
  },
};
</script>

<style scoped>
.page-header { background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); }
.header-icon-wrap {
  width: 48px; height: 48px; background: rgba(255,255,255,0.15);
  border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}

/* Form card */
.form-card { border-radius: 14px; overflow: hidden; border: none; }
.form-card-header {
  display: flex; align-items: center; gap: 12px; padding: 14px 24px;
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
}
.section-icon {
  width: 28px; height: 28px; background: rgba(255,255,255,.2);
  border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #fff; flex-shrink: 0;
}
.form-label { font-weight: 600; font-size: .85rem; color: #374151; margin-bottom: 6px; }
.styled-input { border: 1.5px solid #e2e8f0; border-radius: 8px; font-size: .88rem; }
.styled-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.1); outline: none; }

/* Table card */
.table-card { border-radius: 12px; border: none; }
.table-th {
  font-size: .73rem; text-transform: uppercase; letter-spacing: .04em;
  color: #64748b; font-weight: 600; border-bottom: 2px solid #e2e8f0; padding: 12px 16px;
}
.table-row:hover { background: #f8fafc; }
.row-selected { background: #eff6ff !important; }

/* Section divider */
.section-divider { display: flex; align-items: center; }
.section-divider::before, .section-divider::after { content: ''; flex: 1; height: 1px; background: #e2e8f0; }
.section-divider-text { padding: 0 12px; font-size: .75rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: .06em; }

.btn-submit {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  border: none; color: #fff; border-radius: 8px; font-weight: 600; padding: 8px 20px;
}
.btn-submit:hover { opacity: .9; color: #fff; }
</style>
